/**
 * Cross-sale reference sent with the add to cart request (C#: AddToCartCrossSaleModel).
 */
export interface ICrossSaleRequest {
    RelatedCartEntryId: number;
    TagCode?: string;
    DiscountId?: number;
    DiscountSheetId?: number;
}

/**
 * Reads the cross-sale query parameters added by the shopping cart offers (C#: CrossSaleLinkHelper).
 * The server validates them again on add to cart.
 */
export class CrossSaleRequestHelper {
    static readonly CartEntryParam = 'csEntry';
    static readonly TagCodeParam = 'csTag';
    static readonly DiscountIdParam = 'csDiscountId';
    static readonly DiscountSheetParam = 'csDiscountSheet';

    static getFromUrl(): ICrossSaleRequest | undefined {
        const urlParams = new URLSearchParams(window.location.search);
        const cartEntryId = Number(urlParams.get(CrossSaleRequestHelper.CartEntryParam));

        if (!cartEntryId) {
            return undefined;
        }

        const crossSale: ICrossSaleRequest = {
            RelatedCartEntryId: cartEntryId,
        };

        const tagCode = urlParams.get(CrossSaleRequestHelper.TagCodeParam);
        if (tagCode) {
            crossSale.TagCode = tagCode;
        }

        const discountId = Number(urlParams.get(CrossSaleRequestHelper.DiscountIdParam));
        const discountSheetId = Number(urlParams.get(CrossSaleRequestHelper.DiscountSheetParam));
        if (discountId && discountSheetId) {
            crossSale.DiscountId = discountId;
            crossSale.DiscountSheetId = discountSheetId;
        }

        return crossSale;
    }

    /**
     * Cross-sale query parameters of the current page ("?csEntry=..."), or an empty string when there are none.
     * Used to keep the cross-sale reference when the selection continues on another page (e.g. rental rider details).
     */
    static getQueryString(): string {
        const urlParams = new URLSearchParams(window.location.search);
        const crossSaleParams = new URLSearchParams();

        for (const param of [CrossSaleRequestHelper.CartEntryParam, CrossSaleRequestHelper.TagCodeParam,
            CrossSaleRequestHelper.DiscountIdParam, CrossSaleRequestHelper.DiscountSheetParam]) {
            const value = urlParams.get(param);
            if (value) {
                crossSaleParams.set(param, value);
            }
        }

        const queryString = crossSaleParams.toString();
        return queryString ? `?${queryString}` : '';
    }

    /**
     * Appends the cross-sale query parameters of the current page to <paramref name="url"/> (with "?" or "&").
     */
    static appendToUrl(url: string): string {
        const queryString = CrossSaleRequestHelper.getQueryString();

        if (!queryString) {
            return url;
        }

        return `${url}${url.includes('?') ? '&' : '?'}${queryString.substring(1)}`;
    }

    /**
     * Adds the cross-sale reference of the current page to an add to cart request, if there is one.
     */
    static addToRequest<T extends { CrossSale?: ICrossSaleRequest }>(request: T): T {
        const crossSale = CrossSaleRequestHelper.getFromUrl();

        if (crossSale) {
            request.CrossSale = crossSale;
        }

        return request;
    }
}
